import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { directory, forms, programs } from '@cwos/api-client';
import { Pill, ScopeNote } from '@cwos/ui-admin';
import { SettingsField } from '../settings/common.js';
import { listPhase } from '../listState.js';
import { mayListWorkers, usePermissions } from '../permissions.js';
import { pickerState } from '../pickerState.js';
import { CADENCE_READING, SUBJECT_READING, messageOf } from './formsLogic.js';

/**
 * ASSIGNING A FORM — who is asked this, about whom, and by when.
 *
 * ===========================================================================
 * THE PICKERS ARE THE READER'S OWN REACH, AND THE SCREEN SAYS SO
 * ===========================================================================
 * `GET /directory/clients` and `GET /programs` narrow silently to what the
 * caller may see — a scope miss is `200 []`, not 403 — so a short list is not a
 * fact about the organization. `ScopeNote` states that beside each picker
 * rather than letting a two-name dropdown imply the organization employs two
 * people.
 *
 * `GET /directory/workers` IS NOT ONE OF THEM, and treating it as one was a
 * defect with two halves.
 *
 * It REFUSES rather than narrows: both of its gates are `can()` at
 * organization scope (`directory:read`, then `mayReadWorkerIdentity`'s
 * `user:read`), so a program-scoped holder gets a 403 and not a shorter roster.
 * `mayListWorkers` is the one client-side statement of that rule and this
 * screen was not asking it — the query had NO `enabled:` at all, so it fired
 * for every reader who opened the form.
 *
 * And it fired under the SHARED key `['directory', 'workers']`. Six other
 * modules read that key and every one of them gates it — `Scheduling`,
 * `Visits`, `Timesheets`, `CreateChannel`, `MemberManager`, `EligibilityCenter`
 * — so an ungated fetch here cached a 403 under the key they had correctly
 * declined to ask for, and their next mount read the refusal as their own.
 * Opening this form was enough to break the roster on five other screens.
 *
 * Under the refusal the two staff pickers said "the workers your account can
 * see" over an empty `<select>`, which is the collapse in one sentence: a
 * withheld list rendered as a narrowed one. `pickerState` tells the four
 * answers apart and is what the org-chart pickers already use.
 *
 * ===========================================================================
 * A CLIENT IS SHOWN BY INITIALS, BECAUSE THAT IS WHAT THE ROSTER CARRIES
 * ===========================================================================
 * `directory.Client` has no name on it. The roster projection is initials plus
 * an external reference, and revealing a name is a separate, audited route.
 * This screen does not call it: choosing who an instrument is about does not
 * require a full name on screen, and asking for one would be a disclosure this
 * task does not need.
 *
 * ===========================================================================
 * TWO SUBJECT KINDS HAVE NO PICKER, AND THAT IS STATED RATHER THAN FAKED
 * ===========================================================================
 * `location` and `task` are assignable in the domain and there is no list this
 * screen can offer for either. The honest rendering is to say so; a free-text
 * uuid box would be a worse door, not a better one, because a mistyped uuid
 * answers with a foreign-key refusal that tells the typist nothing.
 */
export interface FormAssignmentProps {
  definition: forms.FormDefinitionView;
  onDone: () => void;
  onCancel: () => void;
}

/** ISO 8601 WITH an offset, which is what `assignFormSchema` requires. */
function toOffsetIso(localValue: string): string | null {
  if (localValue.trim() === '') return null;
  const parsed = new Date(localValue);
  if (Number.isNaN(parsed.getTime())) return null;
  // `datetime-local` yields no zone at all. Interpreting it in the browser's
  // zone and sending UTC is the only reading that survives the round trip; the
  // server refuses a bare local datetime outright.
  return parsed.toISOString();
}

export function FormAssignment({ definition, onDone, onCancel }: FormAssignmentProps) {
  const qc = useQueryClient();
  const permissions = usePermissions();
  const canListWorkers = mayListWorkers(permissions);
  const [subjectKind, setSubjectKind] = useState<forms.FormSubjectKind>(
    definition.subjectKinds[0] ?? 'client',
  );
  const [subjectId, setSubjectId] = useState('');
  const [assignedToUserId, setAssignedToUserId] = useState('');
  const [dueLocal, setDueLocal] = useState('');
  const [cadence, setCadence] = useState<forms.FormCadence>('none');
  const [raiseTask, setRaiseTask] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clients = useQuery({
    queryKey: ['directory', 'clients'],
    queryFn: () => directory.clients(),
    enabled: subjectKind === 'client',
  });
  const workers = useQuery({
    queryKey: ['directory', 'workers'],
    queryFn: () => directory.workers(),
    enabled: canListWorkers,
  });
  const programList = useQuery({
    queryKey: ['programs', 'list', 'for-form-assignment'],
    queryFn: () => programs.list({}),
    enabled: subjectKind === 'program',
  });

  /**
   * WHY THE STAFF PICKER IS EMPTY — the four answers told apart.
   *
   * A refusal claimed only from a RESOLVED set, the same shape `OrgChart`,
   * `ChartEditing`, `DelegationCoverage` and `Superusers` already use:
   * `usePermissions` reads empty while it loads and after it fails, so
   * `!canListWorkers` alone would say "you may not see the workers" to a reader
   * whose set had not arrived. Until it has, the honest phase is `loading`.
   */
  const rosterWithheld = !permissions.isLoading && !permissions.isError && !canListWorkers;
  const staffPicker = pickerState(
    rosterWithheld
      ? { kind: 'refused' }
      : listPhase(
          permissions.isLoading || permissions.isError
            ? { isLoading: true, isError: false, error: null, data: undefined }
            : workers,
          workers.data?.length ?? 0,
        ),
    'workers',
    'Choose…',
  );

  const assign = useMutation({
    mutationFn: () => {
      const dueAt = toOffsetIso(dueLocal);
      return forms.assign(definition.id, {
        subjectKind,
        subjectId,
        ...(assignedToUserId === '' ? {} : { assignedToUserId }),
        ...(dueAt === null ? {} : { dueAt }),
        cadence,
        raiseTask,
      });
    },
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ['forms'] });
      onDone();
    },
    onError: (err: unknown) => setError(messageOf(err)),
  });

  const pickerless = subjectKind === 'location' || subjectKind === 'task';

  return (
    <form
      className="rn-col"
      style={{ gap: 'var(--space-12)', marginTop: 'var(--space-16)' }}
      onSubmit={(e) => {
        e.preventDefault();
        if (subjectId !== '') assign.mutate();
      }}
    >
      <h3 className="rn-card-title">Assign this form</h3>
      <p className="rn-muted">
        An assignment records an obligation — this instrument, about this subject, by this date. It
        does not publish anything and it does not ask the question: only a published version can be
        answered.
      </p>

      <SettingsField label="It is about">
        <select
          value={subjectKind}
          onChange={(e) => {
            setSubjectKind(e.currentTarget.value as forms.FormSubjectKind);
            setSubjectId('');
          }}
        >
          {definition.subjectKinds.map((kind) => (
            <option key={kind} value={kind}>
              {SUBJECT_READING[kind]}
            </option>
          ))}
        </select>
      </SettingsField>

      {pickerless ? (
        <p className="rn-muted" role="note">
          This form can be assigned to {SUBJECT_READING[subjectKind].toLowerCase()}, and this screen
          has no picker for that yet. Rather than offer a box for an id, it says so — a mistyped id
          answers with a refusal that explains nothing.
        </p>
      ) : subjectKind === 'client' ? (
        <>
          <SettingsField label="Which participant">
            <select value={subjectId} onChange={(e) => setSubjectId(e.currentTarget.value)}>
              <option value="">Choose…</option>
              {(clients.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.initials}
                  {c.externalRef === null ? '' : ` · ${c.externalRef}`}
                </option>
              ))}
            </select>
          </SettingsField>
          <ScopeNote scope="the participants your account can see" />
        </>
      ) : subjectKind === 'staff' ? (
        <>
          <SettingsField label="Which staff member">
            <select
              value={subjectId}
              disabled={staffPicker.disabled}
              onChange={(e) => setSubjectId(e.currentTarget.value)}
            >
              <option value="">{staffPicker.placeholder}</option>
              {(workers.data ?? []).map((w) => (
                <option key={w.id} value={w.id}>
                  {w.displayName}
                </option>
              ))}
            </select>
          </SettingsField>
          {staffPicker.note !== null ? (
            <p className="rn-muted" role="status">
              {staffPicker.note}
            </p>
          ) : (
            // The scope caveat belongs ONLY to a list that arrived. Said over a
            // withheld or unread one it renames the refusal as a narrowing.
            <ScopeNote scope="the workers your account can see" />
          )}
        </>
      ) : (
        <>
          <SettingsField label="Which program">
            <select value={subjectId} onChange={(e) => setSubjectId(e.currentTarget.value)}>
              <option value="">Choose…</option>
              {(programList.data?.programs ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </SettingsField>
          <ScopeNote scope="the programs your account can see" />
        </>
      )}

      <SettingsField
        label="Who should answer it"
        hint="Optional. Leaving it unset records the obligation without naming a person."
      >
        <select
          value={assignedToUserId}
          onChange={(e) => setAssignedToUserId(e.currentTarget.value)}
        >
          {/*
            "Nobody in particular" stays selectable in every phase — leaving the
            obligation unassigned is a real choice and does not need the roster.
            What the reader must not be told is that the people they cannot see
            are people who are not there, which is what `staffPicker.note` says
            underneath.
          */}
          <option value="">Nobody in particular</option>
          {(workers.data ?? [])
            .filter((w) => w.userId !== null)
            .map((w) => (
              <option key={w.id} value={w.userId ?? ''}>
                {w.displayName}
              </option>
            ))}
        </select>
        {staffPicker.note !== null ? (
          <p className="rn-muted" role="status">
            {staffPicker.note}
          </p>
        ) : null}
      </SettingsField>

      <div className="rn-row" style={{ gap: 'var(--space-12)', flexWrap: 'wrap' }}>
        <SettingsField
          label="Due"
          hint="Optional. Read in your own timezone and sent with its offset."
        >
          <input
            type="datetime-local"
            value={dueLocal}
            onChange={(e) => setDueLocal(e.currentTarget.value)}
          />
        </SettingsField>
        <SettingsField label="Repeats">
          <select
            value={cadence}
            onChange={(e) => setCadence(e.currentTarget.value as forms.FormCadence)}
          >
            {(Object.keys(CADENCE_READING) as forms.FormCadence[]).map((c) => (
              <option key={c} value={c}>
                {CADENCE_READING[c]}
              </option>
            ))}
          </select>
        </SettingsField>
      </div>

      <label className="rn-row" style={{ gap: 'var(--space-8)', alignItems: 'center' }}>
        <input
          type="checkbox"
          checked={raiseTask}
          onChange={(e) => setRaiseTask(e.currentTarget.checked)}
        />
        <span className="rn-muted">
          Raise a task for it, so it appears in the canonical work queue rather than only here
        </span>
      </label>

      {error ? (
        <p role="alert" style={{ color: 'var(--color-urgent-deep)' }}>
          {error}
        </p>
      ) : null}

      <div className="rn-row" style={{ gap: 'var(--space-8)', alignItems: 'center' }}>
        <button type="submit" className="rn-btn" disabled={assign.isPending || subjectId === ''}>
          Record the assignment
        </button>
        <button
          type="button"
          className="rn-btn-quiet"
          onClick={onCancel}
          disabled={assign.isPending}
        >
          Cancel
        </button>
        {raiseTask ? (
          <Pill variant="sky" dot>
            A task will be raised
          </Pill>
        ) : null}
      </div>
    </form>
  );
}
